import { useEffect, useMemo, useState } from "react";

import {
  useAdoptArtifact,
  useArtifact,
  useArtifactDiff,
  useArtifactLineage,
  useArtifactReviews,
  useArtifactVersions,
  useMarkArtifactStale,
  useReviewArtifact,
  useRollbackArtifact,
  useSources,
  useSubmitArtifact,
  useTransitionArtifact,
  useUpdateArtifact,
} from "@/api/queries";
import type { Artifact, Json } from "@/api/types";
import { Button } from "@/components/Button";
import { DefinitionList } from "@/components/DefinitionList";
import { Field, TextArea, TextInput } from "@/components/Field";
import { Modal } from "@/components/Modal";
import { Panel } from "@/components/Panel";
import { RemoteState } from "@/components/RemoteState";
import { SegmentControl } from "@/components/SegmentControl";
import { ArtifactStatus, StatusLabel } from "@/components/StatusLabel";
import { Tag } from "@/components/Tag";
import {
  contentToMarkdown,
  draftOf,
  fieldKind,
  formatValue,
  parseFieldDraft,
} from "@/lib/artifact-content";
import { useUiStore } from "@/store/ui";

/**
 * Artifact 详情面板（specs/14 §4.5 / 需求 §12.3）。
 *
 * 五项要求 → 五个分区：
 * 1. 结构化表单 / Markdown 双视图 → 「内容」
 * 2. 候选与当前版本差异对比        → 「版本差异」
 * 3. 单字段采纳 / 拒绝 / 编辑      → 「内容」（编辑 + 放弃）与「版本差异」（采纳）
 * 4. 来源引用与依赖查看            → 「来源与依赖」
 * 5. 批准 / 退回 / 锁定 / 回滚     → 「评审」（批准·退回）+ 「版本」（回滚）+ 差异（采纳即锁定）
 *
 * **契约纪律**：这里只呈现，不判定。状态合法性由后端 V102/V103 决定，
 * 后端拒绝时原样抛出（写操作一律不回落，CONSTITUTION D1）。
 */

type Section = "content" | "versions" | "diff" | "lineage" | "reviews";
type ContentMode = "structured" | "markdown";

const SECTIONS: { value: Section; label: string }[] = [
  { value: "content", label: "内容" },
  { value: "versions", label: "版本" },
  { value: "diff", label: "版本差异" },
  { value: "lineage", label: "来源与依赖" },
  { value: "reviews", label: "评审" },
];

const CONTENT_MODES: { value: ContentMode; label: string }[] = [
  { value: "structured", label: "结构化" },
  { value: "markdown", label: "Markdown" },
];

/** 写入动作的失败提示：把后端错误码与原因原样带出来，不吞。 */
function errorText(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error ?? "");
}

export interface ArtifactDetailProps {
  artifactId: string | null;
  onClose: () => void;
}

export function ArtifactDetail({ artifactId, onClose }: ArtifactDetailProps) {
  const id = artifactId ?? "";
  const pushToast = useUiStore((state) => state.pushToast);

  const detail = useArtifact(artifactId);
  const versions = useArtifactVersions(artifactId);
  const lineage = useArtifactLineage(artifactId);
  const reviews = useArtifactReviews(artifactId);

  const [section, setSection] = useState<Section>("content");
  const [mode, setMode] = useState<ContentMode>("structured");
  const [againstId, setAgainstId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [comment, setComment] = useState("");
  const [exceptionReason, setExceptionReason] = useState("");
  const [staleReason, setStaleReason] = useState("");

  const diff = useArtifactDiff(artifactId, againstId);
  const sources = useSources(detail.data?.project_id);

  const update = useUpdateArtifact(id);
  const adopt = useAdoptArtifact(id);
  const submit = useSubmitArtifact(id);
  const review = useReviewArtifact(id);
  const rollback = useRollbackArtifact(id);
  const transition = useTransitionArtifact(id);
  const stale = useMarkArtifactStale(id);

  const artifact = detail.data ?? null;

  // 换产物即重置面板本地状态，避免把上一个产物的草稿带过来。
  useEffect(() => {
    setSection("content");
    setMode("structured");
    setAgainstId(null);
    setDrafts({});
    setFieldErrors({});
    setComment("");
    setExceptionReason("");
    setStaleReason("");
  }, [id]);

  // 进入差异分区时，默认挑一个「不是自己」的最新版本作基线。
  useEffect(() => {
    if (section !== "diff" || againstId) return;
    const pool = (versions.data ?? []).filter((item) => item.id !== artifact?.id);
    if (pool.length) {
      setAgainstId([...pool].sort((a, b) => b.version - a.version)[0].id);
    }
  }, [section, againstId, versions.data, artifact?.id]);

  const sourceTitles = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of sources.data ?? []) map.set(item.id, item.title);
    return map;
  }, [sources.data]);

  const status = artifact?.status ?? "";
  const isLocked = status === "approved";

  function saveField(key: string, value: Json | undefined) {
    const kind = fieldKind(value);
    const raw = drafts[key] ?? draftOf(value);
    let parsed: Json;
    try {
      parsed = parseFieldDraft(raw, kind);
    } catch (error) {
      setFieldErrors((prev) => ({ ...prev, [key]: errorText(error) }));
      return;
    }
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    update.mutate(
      { patch: { [key]: parsed } },
      {
        onSuccess: (row) => {
          setDrafts((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
          pushToast({
            title: `字段已保存 · ${key}`,
            detail:
              row.version > (artifact?.version ?? 0)
                ? `原版本为 approved，已生成 v${row.version}（旧版置为 superseded，V102）`
                : `v${row.version} 内容已更新并刷新 checksum`,
            mark: "✓",
          });
        },
      },
    );
  }

  function decide(decision: "approve" | "reject" | "comment") {
    review.mutate(
      {
        decision,
        comment,
        gate: artifact?.gate ?? null,
        exception_reason: decision === "approve" ? exceptionReason : "",
      },
      {
        onSuccess: (row) => {
          pushToast({
            title:
              decision === "approve" ? "已批准" : decision === "reject" ? "已退回" : "评论已记录",
            detail:
              decision === "comment"
                ? "评论只入审阅记录，不改变状态"
                : `${artifact?.title ?? id} · 当前状态 ${row.status}`,
            mark: decision === "reject" ? "!" : "✓",
          });
          setComment("");
          setExceptionReason("");
        },
      },
    );
  }

  const footer = (
    <>
      <Button variant="quiet" onClick={onClose}>
        关闭
      </Button>
      {status === "draft" || status === "stale" ? (
        <Button
          variant="primary"
          disabled={submit.isPending}
          onClick={() => submit.mutate(undefined)}
        >
          {submit.isPending ? "提交中…" : "提交评审"}
        </Button>
      ) : null}
      {status === "submitted" ? (
        <Button variant="primary" onClick={() => setSection("reviews")}>
          去评审（批准 / 退回）
        </Button>
      ) : null}
      {status === "rejected" ? (
        <Button
          variant="primary"
          disabled={transition.isPending}
          onClick={() =>
            transition.mutate(
              { to_status: "draft", comment: comment || "退回草稿以便修改" },
              {
                onSuccess: () =>
                  pushToast({ title: "已退回草稿", detail: "可编辑后重新提交评审", mark: "✓" }),
              },
            )
          }
        >
          退回草稿
        </Button>
      ) : null}
      {isLocked ? (
        <Button
          variant="primary"
          disabled={stale.isPending}
          onClick={() =>
            stale.mutate(staleReason || "上游变更，人工标记失效", {
              onSuccess: (result) =>
                pushToast({
                  title:
                    result.affected.length > 0
                      ? `已把 ${result.affected.length} 个下游产物置为失效`
                      : "没有下游依赖需要失效",
                  detail:
                    result.affected.length > 0
                      ? "下游产物收到自动失效审阅记录"
                      : "只标记下游：本产物自身状态不变",
                  mark: "!",
                }),
            })
          }
        >
          下游置为失效
        </Button>
      ) : null}
    </>
  );

  return (
    <Modal
      open={Boolean(artifactId)}
      onClose={onClose}
      className="modal-wide"
      eyebrow={artifact ? `${artifact.type} · ${artifact.logical_key}` : "Artifact"}
      title={artifact?.title || artifact?.logical_key || "加载中…"}
      footer={footer}
    >
      <RemoteState queries={[detail]} title="产物详情加载失败">
        {!artifact ? (
          <p className="inline-note">未找到该产物。</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="meta-row">
              <ArtifactStatus status={artifact.status} />
              <Tag tone="blue">v{artifact.version}</Tag>
              {artifact.gate ? <Tag tone="gold">{artifact.gate}</Tag> : null}
              {artifact.stale_reason ? <Tag tone="red">失效：{artifact.stale_reason}</Tag> : null}
              <Tag>{artifact.checksum ? artifact.checksum.slice(0, 16) : "未计算 checksum"}</Tag>
            </div>

            {isLocked ? (
              <p className="inline-note">
                已批准产物不可直接改写（V102）：保存任一字段都会**生成新版本**，并把当前版本置为
                superseded；采纳候选字段同样需要先生成新版本。
              </p>
            ) : null}

            <SegmentControl<Section>
              label="产物分区"
              value={section}
              onChange={setSection}
              options={SECTIONS}
            />

            {section === "content" ? (
              <ContentSection
                artifact={artifact}
                mode={mode}
                onMode={setMode}
                drafts={drafts}
                errors={fieldErrors}
                saving={update.isPending}
                onDraft={(key, value) => setDrafts((prev) => ({ ...prev, [key]: value }))}
                onDiscard={(key) =>
                  setDrafts((prev) => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                  })
                }
                onSave={saveField}
                mutationError={update.error}
              />
            ) : null}

            {section === "versions" ? (
              <RemoteState queries={[versions]} title="版本历史加载失败">
                <VersionSection
                  artifact={artifact}
                  versions={versions.data ?? []}
                  rollingBack={rollback.isPending}
                  onCompare={(targetId) => {
                    setAgainstId(targetId);
                    setSection("diff");
                  }}
                  onRollback={(target) =>
                    rollback.mutate(
                      { target_version: target.version },
                      {
                        onSuccess: (row) =>
                          pushToast({
                            title: `已回滚到 v${target.version}`,
                            detail: `生成 v${row.version}（新版本，不覆盖历史；旧版保留可追溯）`,
                            mark: "✓",
                          }),
                      },
                    )
                  }
                  mutationError={rollback.error}
                />
              </RemoteState>
            ) : null}

            {section === "diff" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Field
                  id="diff-against"
                  label="对比基线"
                  hint="against 是另一份 Artifact 的 ID，不是版本号"
                >
                  <div className="select-wrap">
                    <select
                      id="diff-against"
                      value={againstId ?? ""}
                      onChange={(event) => setAgainstId(event.target.value || null)}
                    >
                      <option value="">选择一个基线版本…</option>
                      {(versions.data ?? []).map((item) => (
                        <option key={item.id} value={item.id}>
                          v{item.version} · {item.status} · {item.id}
                          {item.id === artifact.id ? "（当前）" : ""}
                        </option>
                      ))}
                    </select>
                    <span aria-hidden="true">⌄</span>
                  </div>
                </Field>

                {!againstId ? (
                  <p className="inline-note">先选一个基线版本，才能计算字段级差异。</p>
                ) : (
                  <RemoteState queries={[diff]} title="版本差异加载失败">
                    {diff.data?.identical ? (
                      <p className="inline-note">与所选基线**逐字段一致**，无需采纳。</p>
                    ) : (
                      <ul className="idea-list">
                        {(diff.data?.changes ?? []).map((change) => {
                          const field = String(change.field ?? "");
                          return (
                            <li className="idea-card" key={field}>
                              <div className="idea-meta">
                                <span>{field}</span>
                                <Button
                                  size="small"
                                  disabled={isLocked || adopt.isPending}
                                  onClick={() =>
                                    adopt.mutate(
                                      { candidate_id: againstId, fields: [field] },
                                      {
                                        onSuccess: () =>
                                          pushToast({
                                            title: `已采纳字段 · ${field}`,
                                            detail: `取自基线 ${againstId}，目标 ${artifact.id}`,
                                            mark: "✓",
                                          }),
                                      },
                                    )
                                  }
                                >
                                  采纳
                                </Button>
                              </div>
                              <p>
                                <small>基线：</small>
                                {formatValue(change.from as Json, 160)}
                              </p>
                              <p>
                                <small>当前：</small>
                                {formatValue(change.to as Json, 160)}
                              </p>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </RemoteState>
                )}

                {isLocked ? (
                  <p className="inline-note">
                    当前是已批准版本，采纳会被 V102
                    拒绝。请先编辑任一字段生成新版本，再对新版本采纳。
                  </p>
                ) : null}
                {adopt.isError ? <p className="inline-note">{errorText(adopt.error)}</p> : null}
              </div>
            ) : null}

            {section === "lineage" ? (
              <RemoteState queries={[lineage]} title="血缘加载失败">
                <LineageSection
                  artifact={artifact}
                  lineage={lineage.data}
                  sourceTitles={sourceTitles}
                />
              </RemoteState>
            ) : null}

            {section === "reviews" ? (
              <RemoteState queries={[reviews]} title="评审记录加载失败">
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <Panel
                    title="审阅记录"
                    description="谁在什么时候做了什么决定"
                    bodyClassName="idea-list"
                  >
                    {(reviews.data ?? []).length === 0 ? (
                      <p className="inline-note">还没有审阅记录。</p>
                    ) : (
                      <ul className="activity-list">
                        {(reviews.data ?? []).map((record) => (
                          <li className="activity-item" key={record.id}>
                            <span className="mini-mark">
                              {record.decision === "approve"
                                ? "✓"
                                : record.decision === "reject"
                                  ? "!"
                                  : "¶"}
                            </span>
                            <div>
                              <p>
                                {record.actor_id}（{record.actor_role}）· {record.decision}
                                {record.gate ? ` @ ${record.gate}` : ""}
                              </p>
                              <small>
                                {record.comment || "无意见"}
                                {record.exception_reason
                                  ? ` · 例外：${record.exception_reason}`
                                  : ""}
                              </small>
                              <small>{record.created_at ?? "—"}</small>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Panel>

                  <Panel
                    title="写入评审决定"
                    description="批准 / 退回走状态机（V103），仅评论只入记录"
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      <Field
                        id="review-comment"
                        label="审阅意见"
                        hint={
                          status === "submitted"
                            ? undefined
                            : `当前状态 ${status}，批准/退回可能被 V103 拒绝`
                        }
                      >
                        <TextArea
                          id="review-comment"
                          rows={3}
                          value={comment}
                          placeholder="例如：世界规则与人物底线已收敛，可锁定"
                          onChange={(event) => setComment(event.target.value)}
                        />
                      </Field>
                      <Field
                        id="review-exception"
                        label="例外理由（仅批准时记录）"
                        hint="存在未解决告警但仍需推进时填写，会随导出包交付"
                      >
                        <TextArea
                          id="review-exception"
                          rows={2}
                          value={exceptionReason}
                          onChange={(event) => setExceptionReason(event.target.value)}
                        />
                      </Field>
                      <div className="toolbar-right">
                        <Button
                          size="small"
                          variant="quiet"
                          disabled={review.isPending}
                          onClick={() => decide("comment")}
                        >
                          仅评论
                        </Button>
                        <Button
                          size="small"
                          variant="quiet"
                          disabled={review.isPending}
                          onClick={() => decide("reject")}
                        >
                          退回
                        </Button>
                        <Button
                          size="small"
                          variant="primary"
                          disabled={review.isPending}
                          onClick={() => decide("approve")}
                        >
                          批准
                        </Button>
                      </div>
                      {review.isError ? (
                        <p className="inline-note">{errorText(review.error)}</p>
                      ) : null}
                    </div>
                  </Panel>

                  {isLocked ? (
                    <Field
                      id="stale-reason"
                      label="下游失效理由（传播失效时使用）"
                      hint="后端 PR-124 会把 approved/submitted 的下游置为 stale，本产物自身状态不变"
                    >
                      <TextInput
                        id="stale-reason"
                        value={staleReason}
                        placeholder="例如：上游 Canon 已变更，下游分场需重做"
                        onChange={(event) => setStaleReason(event.target.value)}
                      />
                    </Field>
                  ) : null}
                </div>
              </RemoteState>
            ) : null}
          </div>
        )}
      </RemoteState>
    </Modal>
  );
}

/* ------------------------------------------------------------------ 内容 */

function ContentSection({
  artifact,
  mode,
  onMode,
  drafts,
  errors,
  saving,
  onDraft,
  onDiscard,
  onSave,
  mutationError,
}: {
  artifact: Artifact;
  mode: ContentMode;
  onMode: (mode: ContentMode) => void;
  drafts: Record<string, string>;
  errors: Record<string, string>;
  saving: boolean;
  onDraft: (key: string, value: string) => void;
  onDiscard: (key: string) => void;
  onSave: (key: string, value: Json | undefined) => void;
  mutationError: unknown;
}) {
  const content = artifact.content ?? {};
  const keys = Object.keys(content);

  if (keys.length === 0) {
    return <p className="inline-note">该产物 content 为空。</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <SegmentControl<ContentMode>
        label="内容视图"
        value={mode}
        onChange={onMode}
        options={CONTENT_MODES}
      />

      {mode === "markdown" ? (
        <>
          <p className="inline-note">
            Markdown 是结构化内容的**只读投影**（单向序列化）。反向解析对嵌套结构有损，
            所以编辑统一在「结构化」视图里做。
          </p>
          <pre className="artifact-projection">{contentToMarkdown(content)}</pre>
        </>
      ) : (
        <div className="artifact-fields">
          {keys.map((key) => {
            const value = content[key];
            const kind = fieldKind(value);
            const base = draftOf(value);
            const text = drafts[key] ?? base;
            const dirty = text !== base;
            const fieldId = `artifact-field-${key}`;
            const invalid = errors[key];
            return (
              <div className="form-field" key={key}>
                <Field
                  id={fieldId}
                  label={key}
                  hint={
                    kind === "json"
                      ? "复杂结构：编辑 JSON 后保存，会整体替换该字段"
                      : kind === "boolean"
                        ? "布尔：填 true / false"
                        : kind === "number"
                          ? "数字：留空表示 null"
                          : undefined
                  }
                >
                  {kind === "json" ? (
                    <TextArea
                      id={fieldId}
                      rows={5}
                      value={text}
                      onChange={(event) => onDraft(key, event.target.value)}
                    />
                  ) : (
                    <TextInput
                      id={fieldId}
                      value={text}
                      onChange={(event) => onDraft(key, event.target.value)}
                    />
                  )}
                </Field>
                <div className="toolbar-right">
                  {dirty ? (
                    <Button size="small" variant="quiet" onClick={() => onDiscard(key)}>
                      放弃修改
                    </Button>
                  ) : (
                    <StatusLabel tone="default">未修改</StatusLabel>
                  )}
                  <Button
                    size="small"
                    variant={dirty ? "primary" : "default"}
                    disabled={!dirty || saving}
                    onClick={() => onSave(key, value)}
                  >
                    保存此字段
                  </Button>
                </div>
                {invalid ? <p className="inline-note">{invalid}</p> : null}
              </div>
            );
          })}
        </div>
      )}

      {mutationError ? <p className="inline-note">{errorText(mutationError)}</p> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ 版本 */

function VersionSection({
  artifact,
  versions,
  rollingBack,
  onCompare,
  onRollback,
  mutationError,
}: {
  artifact: Artifact;
  versions: Artifact[];
  rollingBack: boolean;
  onCompare: (artifactId: string) => void;
  onRollback: (target: Artifact) => void;
  mutationError: unknown;
}) {
  if (versions.length === 0) {
    return <p className="inline-note">该逻辑键下没有版本记录。</p>;
  }
  const ordered = [...versions].sort((a, b) => b.version - a.version);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <p className="inline-note">
        版本链按逻辑键 {artifact.logical_key} 维护；回滚不会覆盖历史，而是生成一个新版本。
      </p>
      <ul className="activity-list">
        {ordered.map((item) => (
          <li className="activity-item" key={item.id}>
            <span className="mini-mark">{item.version}</span>
            <div>
              <p>
                v{item.version} · {item.title || item.logical_key}
                {item.id === artifact.id ? " · 当前查看" : ""}
              </p>
              <small>
                {item.status} · {item.checksum ?? "无 checksum"} · {item.updated_at ?? "—"}
              </small>
            </div>
            <div className="story-card-actions">
              {item.id !== artifact.id ? (
                <>
                  <Button size="small" variant="quiet" onClick={() => onCompare(item.id)}>
                    对比
                  </Button>
                  <Button size="small" disabled={rollingBack} onClick={() => onRollback(item)}>
                    回滚到此版
                  </Button>
                </>
              ) : (
                <Tag tone="blue">当前</Tag>
              )}
            </div>
          </li>
        ))}
      </ul>
      {mutationError ? <p className="inline-note">{errorText(mutationError)}</p> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ 血缘 */

function LineageSection({
  artifact,
  lineage,
  sourceTitles,
}: {
  artifact: Artifact;
  lineage:
    | {
        parents?: Record<string, Json>[];
        children?: Record<string, Json>[];
        sources?: string[];
        capability_run_id?: string | null;
      }
    | undefined;
  sourceTitles: Map<string, string>;
}) {
  const parents = lineage?.parents ?? [];
  const children = lineage?.children ?? [];
  const sources = lineage?.sources ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Panel
        title="来源引用"
        description="source_refs · rights_status 未确认不得进入正式版本"
        bodyClassName="idea-list"
      >
        {sources.length === 0 ? (
          <p className="inline-note">该产物未登记来源。</p>
        ) : (
          <ul className="activity-list">
            {sources.map((sourceId) => {
              const title = sourceTitles.get(sourceId);
              return (
                <li className="activity-item" key={sourceId}>
                  <span className="mini-mark">{title ? "¶" : "↑"}</span>
                  <div>
                    <p>{title ?? sourceId}</p>
                    {/* 采纳候选会把候选的 Artifact ID 追加进 source_refs（后端 adopt_fields），
                        它不在资料表里，如实标为产物引用而不是假装是一条资料。 */}
                    <small>{title ? sourceId : "产物引用（采纳候选时写入 source_refs）"}</small>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <Panel title="父版本" description="本产物由哪些上游产物派生" bodyClassName="idea-list">
        {parents.length === 0 ? (
          <p className="inline-note">没有父版本（根产物）。</p>
        ) : (
          <ul className="activity-list">
            {parents.map((parent) => (
              <li className="activity-item" key={String(parent.id)}>
                <span className="mini-mark">↑</span>
                <div>
                  <p>
                    {String(parent.type)} · v{String(parent.version)}
                  </p>
                  <small>
                    {String(parent.id)} · {String(parent.status)}
                  </small>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="下游依赖"
        description="改动本产物会让它们失效（STALE 会传播到下游）"
        bodyClassName="idea-list"
      >
        {children.length === 0 ? (
          <p className="inline-note">没有下游依赖。</p>
        ) : (
          <ul className="activity-list">
            {children.map((child) => (
              <li className="activity-item" key={String(child.id)}>
                <span className="mini-mark">↓</span>
                <div>
                  <p>{String(child.id)}</p>
                  <small>关系 {String(child.edge_type ?? "—")}</small>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="产出与版本信息" description="可复现性所需的全部标识">
        <DefinitionList
          entries={[
            { term: "Artifact", value: artifact.id },
            { term: "schema 版本", value: artifact.schema_version },
            { term: "profile 版本", value: artifact.profile_version ?? "—" },
            { term: "方法论版本", value: artifact.methodology_version ?? "—" },
            { term: "创建者", value: artifact.created_by },
            { term: "生成运行", value: lineage?.capability_run_id ?? "人工创建" },
            { term: "checksum", value: artifact.checksum ?? "—" },
            { term: "创建时间", value: artifact.created_at ?? "—" },
            { term: "更新时间", value: artifact.updated_at ?? "—" },
          ]}
        />
      </Panel>
    </div>
  );
}
